import { zodResolver } from "@hookform/resolvers/zod";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  TooltipProps,
  Typography,
} from "@mui/material";
import {
  ReactElement,
  ReactNode,
  useContext,
  Dispatch,
  SetStateAction,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useAppDispatch } from "#next/redux/reduxHooks";
import {
  addChild,
  BlockType,
  BooleanMode,
  deleteBlock,
  IGroupBlockUI,
  ITextBlockUI,
  selectBlock,
  selectBlockParent,
  selectGroupBlockExclusionDisabledReason,
  selectCanExcludeSavedSearchBlock,
  selectCanExcludeTextBlock,
  selectFilterSettings,
  setExclude,
  setGroupBooleanMode,
  setIsEditing,
  setReferencedSavedSearchId,
  setText,
  selectGroupBlock,
  selectPeerTextBlocksContent,
} from "#next/redux/searchBuilderSlice";
import theme from "#next/theme";
import { MAX_GROUP_DEPTH, zGroupBlock } from "#next/utils/shared";
import { trpc } from "#next/utils/trpc";
import { BooleanPreview } from "#next/components/search-builder/BooleanPreview";
import {
  SavedSearchIdContext,
  useSavedSearchSelector,
} from "#next/components/search-builder/Builder";
import { GroupToolbar } from "#next/components/search-builder/Toolbar";
import { TPreviewState } from "#next/components/search-builder/shared";

type BlockProps = {
  blockId: number;
};

export const ExcludeCheckbox = ({
  isChecked,
  isDisabled,
  disabledReason,
  onChange,
  placement,
}: {
  isChecked: boolean;
  isDisabled: boolean;
  disabledReason: string | null;
  placement: TooltipProps["placement"];
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const tooltipText = (
    <>
      {disabledReason && (
        <>
          <Typography sx={{ color: theme.palette.amber.main }}>
            Exclusion Disabled
          </Typography>
          <span>Exclusion is disabled because </span>
          {disabledReason}
          <Divider sx={{ my: 1 }} />
        </>
      )}
      <span>When selected the content of the block must not be found</span>
    </>
  );

  return (
    <Tooltip title={tooltipText} placement={placement}>
      <FormControlLabel
        label="Exclude"
        control={
          <Checkbox
            disabled={isDisabled}
            checked={isChecked}
            onChange={isDisabled ? () => null : onChange}
          />
        }
      />
    </Tooltip>
  );
};

// A normal tooltip that also works when a component (mostly a button) is disabled
export const TooltipForDisabledComponent = ({
  disabledTooltipText,
  normalTooltipText,
  children,
}: {
  disabledTooltipText: string | null;
  normalTooltipText?: string;
  children: ReactElement;
}) => {
  if (!disabledTooltipText && normalTooltipText) {
    return <Tooltip title={normalTooltipText}>{children}</Tooltip>;
  }
  // The <span> is required to get a Tooltip to work on a disabled button
  // https://mui.com/material-ui/react-tooltip/#disabled-elements
  return (
    <Tooltip title={disabledTooltipText ?? ""}>
      <span>{children}</span>
    </Tooltip>
  );
};

type BlockContainerProps = {
  children: ReactNode;
  remove: () => void;
  isExcludeDisabled: boolean;
  disableExcludeReason: string | null;
  isExcluded: boolean;
  exclude: (shouldExclude: boolean) => void;
  blockName?: string;
};

const BlockContainer = ({
  children,
  remove,
  isExcludeDisabled,
  isExcluded,
  exclude,
  blockName,
  disableExcludeReason,
}: BlockContainerProps) => {
  const { mode } = useContext(SavedSearchIdContext);
  const borderColor = isExcluded ? "red" : "gray";
  return (
    <Card sx={{ m: 1, minWidth: 200, border: "1pt solid", borderColor }}>
      <Grid container alignItems="center" wrap="nowrap">
        {blockName && (
          <Grid item flexShrink={0} sx={{ marginLeft: 2, py: 1 }}>
            <Typography>{blockName}</Typography>
          </Grid>
        )}
        <Grid item sx={{ marginLeft: 2 }}>
          <FormGroup>
            <ExcludeCheckbox
              isChecked={isExcluded}
              onChange={(event) => exclude(event.target.checked)}
              isDisabled={isExcludeDisabled || mode !== "edit"}
              disabledReason={disableExcludeReason}
              placement="right"
            />
          </FormGroup>
        </Grid>
        <Grid item container justifyContent="flex-end">
          <IconButton
            tabIndex={-1} // Prevents field receiving focus from a Tab
            size="small"
            sx={{ color: "#ff8987" }} // TODO: Use color from theme?
            onClick={remove}
            disabled={mode !== "edit"}
          >
            <ClearIcon fontSize="inherit" />
          </IconButton>
        </Grid>
      </Grid>
      <CardContent>{children}</CardContent>
    </Card>
  );
};

const ExactTextBlock = ({ blockId }: BlockProps) => {
  const block = useSavedSearchSelector((state) =>
    selectBlock(state, blockId, "ExactText"),
  );

  const exclusionDisabledReason = useSavedSearchSelector((state) =>
    selectCanExcludeTextBlock(state, block),
  );

  const peerText = useSavedSearchSelector((state) =>
    selectPeerTextBlocksContent(state, block.internalId, "textBlocks"),
  );
  const booleanMode = useSavedSearchSelector(
    (state) => selectBlockParent(state, blockId).booleanMode,
  );

  return (
    <TextBlockBase
      block={block}
      canExcludeBlock={!exclusionDisabledReason}
      exclusionDisabledReason={exclusionDisabledReason}
      peerText={peerText}
      blockType={"ExactText"}
      booleanMode={booleanMode}
    />
  );
};

const StartsWithBlock = ({ blockId }: BlockProps) => {
  const block = useSavedSearchSelector((state) =>
    selectBlock(state, blockId, "StartsWith"),
  );
  const exclusionDisabledReason = useSavedSearchSelector((state) =>
    selectCanExcludeTextBlock(state, block),
  );

  const peerText = useSavedSearchSelector((state) =>
    selectPeerTextBlocksContent(state, block.internalId, "textBlocks"),
  );

  const booleanMode = useSavedSearchSelector(
    (state) => selectBlockParent(state, blockId).booleanMode,
  );

  return (
    <TextBlockBase
      block={block}
      canExcludeBlock={!exclusionDisabledReason}
      exclusionDisabledReason={exclusionDisabledReason}
      peerText={peerText}
      blockType={"StartsWith"}
      booleanMode={booleanMode}
    />
  );
};

const UsernameBlock = ({ blockId }: BlockProps) => {
  const block = useSavedSearchSelector((state) =>
    selectBlock(state, blockId, "Username"),
  );

  const exclusionDisabledReason = useSavedSearchSelector((state) =>
    selectCanExcludeTextBlock(state, block),
  );

  const peerText = useSavedSearchSelector((state) =>
    selectPeerTextBlocksContent(state, block.internalId, "textBlocks"),
  );

  const booleanMode = useSavedSearchSelector(
    (state) => selectBlockParent(state, blockId).booleanMode,
  );

  return (
    <TextBlockBase
      block={block}
      canExcludeBlock={!exclusionDisabledReason}
      exclusionDisabledReason={exclusionDisabledReason}
      peerText={peerText}
      blockType={"Username"}
      booleanMode={booleanMode}
    />
  );
};

type TextBlockTypes = "ExactText" | "StartsWith" | "Username";

const TextBlockBase = ({
  block,
  canExcludeBlock,
  exclusionDisabledReason,
  peerText,
  blockType,
}: {
  block: ITextBlockUI;
  canExcludeBlock: boolean;
  exclusionDisabledReason: string | null;
  peerText: string[];
  blockType: TextBlockTypes;
  booleanMode: BooleanMode;
}) => {
  const dispatch = useAppDispatch();

  let content;

  const blockNames: Record<TextBlockTypes, string> = {
    ["ExactText"]: "Exact Text",
    ["StartsWith"]: "Starts With",
    ["Username"]: "Username",
  };

  const allowsSpaces = ["ExactText", "Username"];
  const allowsAsterisk = ["ExactText", "Username"];

  const zValidator = z.object({
    text: z
      .string()
      .trim()
      .min(2, { message: "Minimum length 2 characters" })
      .refine((value) => !peerText.includes(value), {
        message: "Cannot have duplicate text in the same group",
      })
      .refine((value) => !value.includes('"'), {
        message: 'Text cannot include " character',
      })
      .refine(
        (value) => allowsSpaces.includes(blockType) || !value.includes(" "),
        { message: "Cannot have multiple words" },
      )
      .refine(
        (value) => allowsAsterisk.includes(blockType) || !value.endsWith("*"),
        {
          message: "Remove unnecessary * character",
        },
      ),
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "all",
    defaultValues: { text: block.value ?? "" },
    resolver: zodResolver(zValidator),
  });

  const errorMessage = errors.text?.message;

  const { savedSearchId, mode } = useContext(SavedSearchIdContext);

  const submit = handleSubmit(({ text }) =>
    dispatch(
      setText({ blockId: block.internalId, blockType, text, savedSearchId }),
    ),
  );

  if (block.isEditing) {
    content = (
      <Controller
        name="text"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            autoFocus
            placeholder="Enter search term"
            error={!!errorMessage}
            helperText={errorMessage}
            onKeyPress={(event) => {
              if (event.key === "Enter" && !errorMessage) {
                submit();
              }
            }}
            onKeyUp={(event) => {
              if (event.key === "Escape") {
                if (block.value) {
                  reset();
                  dispatch(
                    setIsEditing({
                      blockId: block.internalId,
                      blockType,
                      isEditing: false,
                      savedSearchId,
                    }),
                  );
                } else {
                  dispatch(
                    deleteBlock({
                      blockId: block.internalId,
                      blockType,
                      savedSearchId,
                    }),
                  );
                }
              }
            }}
            onBlur={() => {
              if (!errorMessage) {
                submit();
              }
            }}
          />
        )}
      />
    );
  } else {
    content = (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Tooltip title={mode === "edit" && "Click to edit text"}>
          <Chip
            // Makes the height consistent with Saved Search selector. Might be a better way
            sx={{ height: "40px" }}
            label={block.value}
            onClick={
              mode === "edit"
                ? () => {
                    dispatch(
                      setIsEditing({
                        blockId: block.internalId,
                        blockType,
                        isEditing: true,
                        savedSearchId,
                      }),
                    );
                  }
                : undefined
            }
          />
        </Tooltip>
      </Box>
    );
  }
  return (
    <BlockContainer
      remove={() =>
        dispatch(
          deleteBlock({
            blockId: block.internalId,
            blockType,
            savedSearchId,
          }),
        )
      }
      isExcludeDisabled={!canExcludeBlock}
      exclude={(shouldExclude) =>
        dispatch(
          setExclude({
            blockId: block.internalId,
            exclude: shouldExclude,
            blockType,
            savedSearchId,
          }),
        )
      }
      isExcluded={block.exclude}
      disableExcludeReason={exclusionDisabledReason}
      blockName={blockNames[blockType]}
    >
      {content}
    </BlockContainer>
  );
};

const SavedSearchBlock = ({ blockId }: BlockProps) => {
  const searches = trpc.savedSearch.list.useQuery();
  const dispatch = useAppDispatch();

  const block = useSavedSearchSelector((state) =>
    selectBlock(state, blockId, "SavedSearch"),
  );
  const exclusionDisabledReason = useSavedSearchSelector((state) =>
    selectCanExcludeSavedSearchBlock(state, block),
  );

  const { savedSearchId } = useContext(SavedSearchIdContext);
  const allSearches = searches.data
    ? [...searches.data.ownedByUser, ...searches.data.sharedWithUser].filter(
        (search) => search.id !== savedSearchId,
      )
    : [];

  const selectedSearch = allSearches.find(
    (search) => search.id === block.savedSearchId,
  );

  return (
    <BlockContainer
      remove={() =>
        dispatch(
          deleteBlock({
            blockId,
            blockType: "SavedSearch",
            savedSearchId,
          }),
        )
      }
      isExcludeDisabled={!!exclusionDisabledReason}
      exclude={(shouldExclude) =>
        dispatch(
          setExclude({
            blockId,
            exclude: shouldExclude,
            blockType: "SavedSearch",
            savedSearchId,
          }),
        )
      }
      isExcluded={block.exclude}
      disableExcludeReason={exclusionDisabledReason}
    >
      <FormControl size="small">
        <InputLabel id={`${blockId}`}>Project</InputLabel>
        <Select
          labelId={`${blockId}`}
          label="Project"
          value={selectedSearch?.id ?? ""}
          onChange={(event) =>
            dispatch(
              setReferencedSavedSearchId({
                blockId,
                savedSearchId,
                referencedSavedSearchId: event.target.value,
              }),
            )
          }
          sx={{ minWidth: 175 }}
        >
          {allSearches.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </BlockContainer>
  );
};

export const blockCanGenerateSearch = (
  block: IGroupBlockUI,
  filterSettingsAreValid: boolean,
) => {
  const reasonsWhyNot = new Set<string>();
  const computeReasons = (_block: IGroupBlockUI) => {
    if (_block.textBlocks.some((tb) => !tb.value)) {
      reasonsWhyNot.add("One or more Exact Text blocks have no text");
    }
    if (_block.textBlocks.some((tb) => tb.isEditing)) {
      reasonsWhyNot.add("One or more Exact Text blocks is in edit mode");
    }
    if (_block.startsWithBlocks.some((tb) => !tb.value)) {
      reasonsWhyNot.add("One or more Starts With blocks have no text");
    }
    if (_block.startsWithBlocks.some((tb) => tb.isEditing)) {
      reasonsWhyNot.add("One or more Starts With blocks is in edit mode");
    }
    if (_block.savedSearchBlocks.some((ssb) => !ssb.savedSearchId)) {
      reasonsWhyNot.add(
        "One or more Project blocks have not selected a project",
      );
    }
    if (_block.usernameBlocks.some((tb) => !tb.value)) {
      reasonsWhyNot.add("One or more Username blocks have no text");
    }
    if (
      _block.textBlocks.length +
        _block.startsWithBlocks.length +
        _block.savedSearchBlocks.length +
        _block.usernameBlocks.length +
        _block.groupBlocks.length ===
      0
    ) {
      reasonsWhyNot.add("One or more Group blocks have no children");
    }
    _block.groupBlocks.forEach(computeReasons);
  };
  computeReasons(block);
  if (!filterSettingsAreValid) {
    reasonsWhyNot.add("Filter settings are not valid");
  }
  return {
    canSearch: reasonsWhyNot.size === 0,
    reason: `${Array.from(reasonsWhyNot).join(". ")}${
      reasonsWhyNot.size ? "." : ""
    }`,
  };
};

export const GroupBlock = ({
  blockId,
  setPreviewState,
}: {
  blockId: number;
  setPreviewState: Dispatch<SetStateAction<TPreviewState>>;
}) => {
  const { block, depth } = useSavedSearchSelector((state) =>
    selectGroupBlock(state, blockId),
  );
  const { settings: filterSettings, settingsAreValid: filterSettingsAreValid } =
    useSavedSearchSelector(selectFilterSettings);

  const exclusionDisabledReason = useSavedSearchSelector((state) =>
    selectGroupBlockExclusionDisabledReason(state, block),
  );
  const { canSearch, reason } = blockCanGenerateSearch(
    block,
    filterSettingsAreValid,
  );
  const dispatch = useAppDispatch();

  const handleSelectPreview = () => {
    if (canSearch && filterSettingsAreValid) {
      setPreviewState({
        groupBlock: zGroupBlock.parse(block),
        filterSettings: filterSettings,
        isPreviewing: true,
      });
    }
  };

  const { savedSearchId } = useContext(SavedSearchIdContext);

  const borderColor = block.exclude ? "red" : "gray"; // TODO: Use theme colours. Or add a red to theme if needed.
  return (
    <Card sx={{ m: 1, minWidth: 200, border: "1pt solid", borderColor }}>
      <Box sx={{ backgroundColor: `${theme.palette.toolbar.main}`, m: 0 }}>
        <GroupToolbar
          createBlock={(childType: BlockType) =>
            dispatch(addChild({ blockId, childType, savedSearchId }))
          }
          setBooleanMode={(booleanMode: BooleanMode) =>
            dispatch(
              setGroupBooleanMode({ blockId, booleanMode, savedSearchId }),
            )
          }
          booleanPreview={<BooleanPreview block={block} />}
          isExcluded={block.exclude}
          setExclude={(exclude: boolean) =>
            dispatch(
              setExclude({
                blockId,
                exclude,
                blockType: "Group",
                savedSearchId,
              }),
            )
          }
          booleanMode={block.booleanMode}
          disableAddGroupControl={depth === MAX_GROUP_DEPTH}
          disablePreview={!canSearch}
          disablePreviewReason={reason}
          disableExclude={!!exclusionDisabledReason}
          usernameBlocks={block.usernameBlocks.length}
          isRootGroup={block.isRoot}
          disableExcludeReason={exclusionDisabledReason}
          delete={() =>
            dispatch(
              deleteBlock({
                blockId,
                blockType: "Group",
                savedSearchId,
              }),
            )
          }
          preview={handleSelectPreview}
        />
      </Box>
      <Grid container>
        {block.textBlocks.map(({ internalId: childId }) => {
          return (
            <Grid item key={childId} sx={{ m: 1 }}>
              <ExactTextBlock blockId={childId} />
            </Grid>
          );
        })}
        {block.startsWithBlocks.map(({ internalId: childId }) => {
          return (
            <Grid item key={childId} sx={{ m: 1 }}>
              <StartsWithBlock blockId={childId} />
            </Grid>
          );
        })}
        {block.usernameBlocks.map(({ internalId: childId }) => {
          return (
            <Grid item key={childId} sx={{ m: 1 }}>
              <UsernameBlock blockId={childId} />
            </Grid>
          );
        })}
        {block.savedSearchBlocks.map(({ internalId: childId }) => {
          return (
            <Grid item key={childId} sx={{ m: 1 }}>
              <SavedSearchBlock blockId={childId} />
            </Grid>
          );
        })}
        {block.groupBlocks.map(({ internalId: childId }) => {
          return (
            <Grid item key={childId} sx={{ m: 1 }} xs={12}>
              <GroupBlock blockId={childId} setPreviewState={setPreviewState} />
            </Grid>
          );
        })}
      </Grid>
    </Card>
  );
};
